import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { getBoundArrivalCity, getBoundDepartureCity } from '@/app/components/hotel-detail/flight-train/TransportCommon';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';

export type StopsFilterChange = {
  newState: {
    outboundStops?: StopsFilter[];
    inboundStops?: StopsFilter[];
    directOnly?: DirectOnlyFilter;
  };
  event: StopsFilterEvent;
};

export type TimeSlotsFilterChange = {
  newState: { inboundDepartureTimeSlots: TimeSlotFilter[] } | { outboundDepartureTimeSlots: TimeSlotFilter[] };
  event: TimeSlotFilterEvent;
};

export type AirportFilterType =
  'outboundDepartureAirports' | 'outboundArrivalAirports' | 'inboundArrivalAirports' | 'inboundDepartureAirports';

export type AirportsFilterChange = {
  newState: Record<AirportFilterType, AirportFilter[]>;
  event: AirportsFilterEvent;
};

export type CompaniesFilterChange = {
  newState: { airlines: CompanyFilter[] } | { companies: CompanyFilter[] };
  event: CompaniesFilterEvent;
};

export type SameAirlineFilterChange = {
  newState: { sameAirline: SameAirportFilter };
  event: CompaniesFilterEvent;
};

export type SameAirportInboundFilterChange = {
  newState: { inboundSameAirport: SameAirportFilter };
  event: SameAirportFilterEvent;
};

export type SameAirportOutboundFilterChange = {
  newState: { outboundSameAirport: SameAirportFilter };
  event: SameAirportFilterEvent;
};

export type AirportsClearAllFilterChange = {
  newState: Record<AirportFilterType, AirportFilter[] | undefined> & { inboundSameAirport: SameAirportFilter };
  event: AirportsClearAllFilterEvent;
};

export type SortFilterChange = {
  newState: { sortOptions: SortFilter[] };
  event: SortFilterEvent;
};

export type HandBaggageFilterChange = {
  newState: { handBaggageIncluded: BaggageFilter[] };
  event: BaggageFilterEvent;
};

export type BaggageFilterChange = {
  newState: { baggageIncluded: BaggageFilter[] };
  event: BaggageFilterEvent;
};

export type ClearAllFilterChange = {
  newState?: undefined;
  event: ClearAllFilterEvent;
};

export type FilterChange =
  | StopsFilterChange
  | TimeSlotsFilterChange
  | AirportsFilterChange
  | SameAirportInboundFilterChange
  | SameAirportOutboundFilterChange
  | CompaniesFilterChange
  | SameAirlineFilterChange
  | AirportsClearAllFilterChange
  | SortFilterChange
  | HandBaggageFilterChange
  | BaggageFilterChange
  | ClearAllFilterChange;

export const Filter = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
`;

export const FilterContainer = styled.div`
  font-size: ${(p) => p.theme.kallisto.font.size['175']};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
`;

export const FilterHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

export const FilterTitle = styled(Text).attrs({ variant: 'Title4' })``;

export function FilterPrice(props: { price: number }) {
  const { price } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  return (
    <span
      css={css`
        font-size: ${(p) => p.theme.kallisto.font.size['150']};
        color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
        min-height: 20px;
      `}
    >
      {price !== 0 ? `${t('vacation.common.price.from')} ${formatPrice({ price, localization })}` : undefined}
    </span>
  );
}

export function BoundHeader(props: { type: BoundType }) {
  const { type } = props;
  const { data: selectedTransportData } = useSelectedTransport();
  const departureCityName = selectedTransportData
    ? getBoundDepartureCity(selectedTransportData?.transports[0].outbound)
    : undefined;
  const arrivalCityName = selectedTransportData
    ? getBoundArrivalCity(selectedTransportData?.transports[0].outbound)
    : undefined;
  const { t } = useTranslation();
  const origin = departureCityName;
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        padding-bottom: 0.5rem;
      `}
    >
      <Text variant="Title5">
        {type === 'outbound' ? t('vacation.journey.outbound') : t('vacation.journey.inbound')}
      </Text>
      <span
        css={css`
          color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
        `}
      >{`${type === 'outbound' ? origin : arrivalCityName} - ${type === 'inbound' ? origin : arrivalCityName}`}</span>
    </div>
  );
}
